import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { fmtMoney, fmtHours, fmtNumber, fmtDate } from '../components/Overtime/format';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

export default function OvertimeUpload() {
  const qc = useQueryClient();
  const [file,     setFile]     = useState(null);
  const [progress, setProgress] = useState(0);
  const [preview,  setPreview]  = useState(null);   // { batch_id, preview: {...} }
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['ot-batches'],
    queryFn:  () => api.get('/overtime/batches'),
    refetchInterval: 15000,
  });

  // ── Step 1: upload + parse (preview only) ──────────────────
  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/overtime/upload', fd, setProgress);
    },
    onSuccess: (data) => { setPreview(data); setProgress(0); },
    onError:   (err) => { toast.error(err.message || 'Upload failed.'); setProgress(0); },
  });

  // ── Step 2a: confirm ───────────────────────────────────────
  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/overtime/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(`Committed ${fmtNumber(data.rowCount)} rows (${fmtNumber(data.otRowCount)} overtime).`);
      resetAll();
      qc.invalidateQueries({ queryKey: ['ot-batches'] });
      qc.invalidateQueries({ queryKey: ['ot-periods'] });
      qc.invalidateQueries({ queryKey: ['ot-summary'] });
    },
    onError: (err) => toast.error(err.message || 'Confirm failed.'),
  });

  // ── Step 2b: cancel ────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: () => api.post('/overtime/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => { toast('Upload discarded.'); resetAll(); qc.invalidateQueries({ queryKey: ['ot-batches'] }); },
    onError:   (err) => toast.error(err.message || 'Cancel failed.'),
  });

  function resetAll() {
    setFile(null); setPreview(null); setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview;
  const busy = uploadMutation.isPending || confirmMutation.isPending || cancelMutation.isPending;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Overtime Upload"
        subtitle="Import a payroll export. The file is parsed and previewed first — nothing is saved until you confirm. Pay rate is never stored."
      />

      {/* ── Step 1: drop zone (hidden once we have a preview) ── */}
      {!preview && (
        <div style={s.card}>
          <div
            style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : '#D1D5DB' }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            <UploadCloud size={26} color={file ? '#16A34A' : '#9CA3AF'} />
            <div style={s.dropText}>
              {file ? (
                <><strong style={{ color: '#16A34A' }}>{file.name}</strong><br /><span style={{ color: '#9CA3AF' }}>{(file.size / 1024).toFixed(0)} KB</span></>
              ) : (
                <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: '#9CA3AF' }}>Payroll .xlsx or .xls</span></>
              )}
            </div>
          </div>

          {uploadMutation.isPending && (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
              <span style={s.progressLabel}>{progress}%</span>
            </div>
          )}

          <button
            style={{ ...s.primaryBtn, opacity: file && !busy ? 1 : 0.5 }}
            disabled={!file || busy}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? 'Parsing…' : 'Parse & Preview'}
          </button>
        </div>
      )}

      {/* ── Step 2: preview + confirm/cancel ─────────────────── */}
      {p && (
        <div style={s.card}>
          <div style={s.previewHead}>
            <FileSpreadsheet size={18} color="#1B2A4A" />
            <span style={s.previewTitle}>Preview — {preview.file_name || file?.name || 'payroll file'}</span>
          </div>

          <div style={s.statGrid}>
            <Stat label="Total Rows"    value={fmtNumber(p.total_rows)} />
            <Stat label="Overtime Rows" value={fmtNumber(p.ot_rows)} accent="#A16207" />
            <Stat label="Employees w/ OT" value={fmtNumber(p.ot_employee_count)} />
            <Stat label="OT Hours"      value={fmtHours(p.ot_total_hours)} />
            <Stat label="OT Amount"     value={fmtMoney(p.ot_total_amount)} accent="#DC2626" />
            <Stat label="Cost Centers"  value={fmtNumber(p.cost_center_codes?.length || 0)} />
          </div>

          {/* Detected periods */}
          {p.periods?.length > 0 && (
            <div style={s.subBlock}>
              <div style={s.subLabel}>Pay Periods Detected</div>
              <div style={s.chips}>
                {p.periods.map((pe) => (
                  <span key={pe.period_end} style={s.chip}>{fmtDate(pe.period_end)} · {fmtNumber(pe.rows)} rows</span>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {p.warning_count > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> {fmtNumber(p.warning_count)} warning(s)</div>
              <ul style={s.warnList}>
                {p.warnings.slice(0, 8).map((w, i) => <li key={i} style={s.warnItem}>{w}</li>)}
                {p.warning_count > 8 && <li style={s.warnItem}>…and {fmtNumber(p.warning_count - 8)} more.</li>}
              </ul>
            </div>
          )}

          {/* Sample rows */}
          {p.sample?.length > 0 && (
            <div style={s.subBlock}>
              <div style={s.subLabel}>Sample Rows</div>
              <div style={s.sampleWrap}>
                <table style={s.sampleTable}>
                  <thead>
                    <tr>
                      {['Employee', 'CC', 'Income Type', 'Period End', 'Hours', 'Amount', 'OT?'].map((h) => (
                        <th key={h} style={s.sTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {p.sample.map((r, i) => (
                      <tr key={i} style={s.sTr}>
                        <td style={s.sTd}>{r.employee_name || r.employee_number}</td>
                        <td style={s.sTd}>{r.cost_center_code}</td>
                        <td style={s.sTd}>{r.income_desc || '—'}</td>
                        <td style={s.sTd}>{fmtDate(r.period_end)}</td>
                        <td style={s.sTdR}>{fmtHours(r.hours)}</td>
                        <td style={s.sTdR}>{fmtMoney(r.amount)}</td>
                        <td style={s.sTd}>{r.is_overtime ? <span style={s.otTag}>OT</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={s.actionRow}>
            <button style={{ ...s.primaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy}
                    onClick={() => confirmMutation.mutate()}>
              <CheckCircle size={15} /> {confirmMutation.isPending ? 'Committing…' : 'Confirm & Commit'}
            </button>
            <button style={{ ...s.secondaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy}
                    onClick={() => cancelMutation.mutate()}>
              <XCircle size={15} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Ingestion history ────────────────────────────────── */}
      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <table style={s.logTable}>
            <thead>
              <tr>{['File', 'Source', 'Status', 'Rows', 'OT Rows', 'By', 'When'].map((h) => <th key={h} style={s.logTh}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const st = STATUS_STYLE[b.status] || STATUS_STYLE.preview;
                return (
                  <tr key={b.id} style={s.logTr}>
                    <td style={s.logTd} title={b.file_name}>{b.file_name?.slice(-40)}</td>
                    <td style={s.logTd}>{b.source === 'ftp' ? '⟳ FTP' : '↑ Manual'}</td>
                    <td style={s.logTd}><span style={{ ...s.statusPill, color: st.color, background: st.bg }}>{st.icon} {b.status}</span></td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{fmtNumber(b.row_count)}</td>
                    <td style={{ ...s.logTd, textAlign: 'right' }}>{fmtNumber(b.ot_row_count)}</td>
                    <td style={s.logTd}>{b.uploaded_by_name || (b.source === 'ftp' ? 'System' : '—')}</td>
                    <td style={s.logTd}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
              {!batches.length && <tr><td colSpan={7} style={s.emptyLog}>No imports yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={s.stat}>
      <div style={{ ...s.statVal, ...(accent ? { color: accent } : {}) }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: '#16A34A', bg: '#DCFCE7', icon: <CheckCircle size={12} /> },
  preview:   { color: '#1D4ED8', bg: '#EFF6FF', icon: <AlertTriangle size={12} /> },
  cancelled: { color: '#9CA3AF', bg: '#F3F4F6', icon: <XCircle size={12} /> },
  failed:    { color: '#DC2626', bg: '#FEE2E2', icon: <XCircle size={12} /> },
};

const s = {
  page:        { maxWidth: '900px', margin: '0 auto' },
  card:        { ...card.base, padding: '24px', marginBottom: '24px' },
  dropZone:    { border: '2px dashed', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', transition: 'border-color 0.2s' },
  dropText:    { fontSize: '13px', color: '#374151', lineHeight: 1.6 },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: '#1B2A4A', borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '11px', fontWeight: 600, color: '#374151', minWidth: '30px' },
  primaryBtn:  { ...btn.primary },
  secondaryBtn:{ ...btn.secondary },
  previewHead: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' },
  previewTitle:{ fontSize: '14px', fontWeight: 700, color: '#111827' },
  statGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' },
  stat:        { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '12px 14px' },
  statVal:     { fontSize: '18px', fontWeight: 700, color: '#111827' },
  statLabel:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF', marginTop: '3px' },
  subBlock:    { marginBottom: '18px' },
  subLabel:    { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', marginBottom: '8px' },
  chips:       { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip:        { fontSize: '12px', color: '#374151', background: '#EEF2F7', borderRadius: '6px', padding: '4px 10px' },
  warnBlock:   { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' },
  warnHead:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#A16207', marginBottom: '6px' },
  warnList:    { margin: 0, paddingLeft: '18px' },
  warnItem:    { fontSize: '12px', color: '#92400E', lineHeight: 1.6 },
  sampleWrap:  { border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' },
  sampleTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  sTh:         { ...tbl.th, ...tbl.thSm },
  sTr:         { borderBottom: '1px solid #F3F4F6' },
  sTd:         { ...tbl.td, ...tbl.tdSm },
  sTdR:        { ...tbl.tdR, ...tbl.tdRSm },
  otTag:       { fontSize: '9px', fontWeight: 700, color: '#A16207', background: '#FEF3C7', padding: '1px 5px', borderRadius: '4px' },
  actionRow:   { display: 'flex', gap: '10px', marginTop: '6px' },
  logSection:  { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' },
  logTitle:    { fontSize: '13px', fontWeight: 600, color: '#374151', padding: '14px 18px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  logTable:    { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  logTh:       { ...tbl.th, ...tbl.th },
  logTr:       { borderBottom: '1px solid #F3F4F6' },
  logTd:       { ...tbl.td, ...tbl.td },
  statusPill:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: '#9CA3AF' },
};
